import { FC, KeyboardEvent, ReactNode, useEffect, useState } from "react";

import {
  Alert,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { DataGrid, GridCellParams, GridColDef } from "@mui/x-data-grid";
import {
  GeoJsonGeometry,
  getGeoDataFileProcessingRun,
  RegionWithSubregions,
  ResolutionTier,
  SubregionModel,
} from "@rubi/api-generated";
import { Link, useNavigate } from "react-router-dom";

import { Breadcrumbs, Crumb, ErrorMsg, Section } from "./common";
import { RegionGeometryMap } from "./RegionGeometryMap";

/**
 * DataGrid's own keyboard navigation moves focus between cells but doesn't
 * turn Enter/Space into a row click. Pair with `onRowClick` on every
 * navigable grid so keyboard/screen-reader users can open a row too.
 */
export const onRowActivateKeyDown =
  (onActivate: (id: string) => void) =>
  (params: GridCellParams, event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onActivate(String(params.id));
  };

/** Breadcrumb trail shared by every org-scoped Regions page. */
export const RegionBreadcrumbs: FC<{ orgId: string; children?: ReactNode }> = ({
  orgId,
  children,
}) => (
  <Breadcrumbs>
    <Crumb>
      <Link to="/">Home</Link>
    </Crumb>
    <Crumb>
      <Link to="/dev_admin">Developer and Administrator Utilities</Link>
    </Crumb>
    <Crumb>
      <Link to="/dev_admin/region">Regions</Link>
    </Crumb>
    <Crumb>
      <Link to={`/dev_admin/region/${orgId}/geo_data_file`}>{orgId}</Link>
    </Crumb>
    {children}
  </Breadcrumbs>
);

type RegionSection = "geo_data_file" | "subregion" | "region";

const SECTIONS: { key: RegionSection; label: string }[] = [
  { key: "geo_data_file", label: "GeoDataFiles" },
  { key: "subregion", label: "Subregions" },
  { key: "region", label: "Regions" },
];

/** Three-way tab bar between the org-scoped GeoDataFile/Subregion/Region lists. */
export const RegionSubNav: FC<{ orgId: string; active: RegionSection }> = ({
  orgId,
  active,
}) => (
  <Section>
    <nav style={{ display: "flex", gap: 16 }}>
      {SECTIONS.map((section) => (
        <Link
          key={section.key}
          to={`/dev_admin/region/${orgId}/${section.key}`}
          style={{ fontWeight: section.key === active ? 700 : 400 }}
        >
          {section.label}
        </Link>
      ))}
    </nav>
  </Section>
);

export interface RegionArea {
  area: GeoJsonGeometry;
  bounds: GeoJsonGeometry;
}

const TIERS: ResolutionTier[] = ["low", "medium", "full"];

/**
 * "Map" section for a Subregion/Region detail page: a low/medium/full tier
 * toggle (default `medium` — `full` is for server-side point lookups and is
 * heavy to draw) over a `RegionGeometryMap` of the selected tier.
 *
 * `fetchArea` must be referentially stable (wrap it in `useCallback`) — a new
 * identity triggers a re-fetch. Bump `reloadKey` to re-fetch the current tier
 * after the geometry has changed server-side.
 */
export const RegionAreaMapSection: FC<{
  fetchArea: (tier: ResolutionTier) => Promise<RegionArea | undefined>;
  reloadKey?: unknown;
}> = ({ fetchArea, reloadKey }) => {
  const [tier, setTier] = useState<ResolutionTier>("medium");
  const [area, setArea] = useState<RegionArea | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Ignore responses that land after the tier (or target) has moved on, so
    // a slow `full` fetch can't overwrite a quicker `low` one picked after it.
    let stale = false;
    setError(null);
    fetchArea(tier).then(
      (next) => {
        if (!stale) setArea(next ?? null);
      },
      (e) => {
        if (!stale) {
          setError(e instanceof Error ? e.message : "Failed to load geometry");
        }
      },
    );
    return () => {
      stale = true;
    };
  }, [fetchArea, tier, reloadKey]);

  return (
    <Section subtitle="Map">
      <Stack spacing={1}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={tier}
          onChange={(_, next: ResolutionTier | null) => next && setTier(next)}
          aria-label="Geometry resolution tier"
        >
          {TIERS.map((t) => (
            <ToggleButton key={t} value={t}>
              {t}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <ErrorMsg msg={error} />
        {area ? (
          <RegionGeometryMap geoJson={area.area} bounds={area.bounds} />
        ) : (
          !error && <Alert severity="info">Loading geometry…</Alert>
        )}
      </Stack>
    </Section>
  );
};

/**
 * Table of an org's Regions; a row opens the Region. `activeRegionId` marks
 * the org's active Region with a chip.
 */
export const RegionGrid: FC<{
  orgId: string;
  regions: RegionWithSubregions[];
  activeRegionId: string | null;
  loading?: boolean;
}> = ({ orgId, regions, activeRegionId, loading }) => {
  const navigate = useNavigate();

  const columns: GridColDef<RegionWithSubregions>[] = [
    { field: "id", headerName: "ID", flex: 1, minWidth: 280 },
    {
      field: "subregions",
      headerName: "Subregions",
      width: 120,
      valueGetter: (value: SubregionModel[]) => value.length,
    },
    {
      field: "time_created",
      headerName: "Time Created",
      width: 200,
      valueFormatter: (value: string) => new Date(value).toLocaleString(),
    },
    {
      field: "active",
      headerName: "",
      width: 100,
      sortable: false,
      renderCell: (params) =>
        params.row.id === activeRegionId ? (
          <Chip size="small" color="success" label="Active" />
        ) : null,
    },
  ];

  const openRegion = (regionId: string) =>
    navigate(`/dev_admin/region/${orgId}/region/${regionId}`);

  return (
    <div style={{ width: "100%" }}>
      <DataGrid
        rows={regions}
        columns={columns}
        loading={loading}
        autoHeight
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        pageSizeOptions={[10, 25, 50]}
        onRowClick={(params) => openRegion(String(params.id))}
        onCellKeyDown={onRowActivateKeyDown(openRegion)}
        sx={{ cursor: "pointer" }}
      />
    </div>
  );
};

/**
 * A processing run's id, linked to its run page. That URL also needs the
 * GeoDataFile id, which Regions/Subregions don't carry, so this looks the run
 * up first — plain text until it resolves (or if it can't).
 */
export const ProcessingRunLink: FC<{
  orgId: string;
  runId?: string | null;
}> = ({ orgId, runId }) => {
  const [fileId, setFileId] = useState<string | null>(null);

  useEffect(() => {
    setFileId(null);
    if (!runId) return;
    let stale = false;
    getGeoDataFileProcessingRun({
      path: { org_id: orgId, run_id: runId },
      throwOnError: false,
    }).then((response) => {
      if (!stale) setFileId(response.data?.geo_data_file_id ?? null);
    });
    return () => {
      stale = true;
    };
  }, [orgId, runId]);

  if (!runId) return null;
  if (!fileId) return <>{runId}</>;
  return (
    <Link
      to={`/dev_admin/region/${orgId}/geo_data_file/${fileId}/run/${runId}`}
    >
      {runId}
    </Link>
  );
};
